import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { JobDetailsInterface } from 'screens/JobTagsScreen/jobScreenTypes';
import { getAllJobsServices } from 'services/JobTagScreenServices/getAllJobsService';
import { handleApiError } from 'utils/handleApiError';

export class JobTagsStore {
  isAllJobsLoading: boolean = false;
  isJobDetailsLoading: boolean = false;
  isPollingJobs: boolean = false;
  isPollingJobDetails: boolean = false;
  allJobs: string[] = [];
  selectedJobId: string = '';
  jobDetails: JobDetailsInterface | null = null;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedJobId = (value: string) => {
    this.selectedJobId = value;
  };

  // Initial load methods (with loading indicators)
  fetAllJobs = async () => {
    this.isAllJobsLoading = true;
    this.isJobDetailsLoading = true;
    try {
      const response = await getAllJobsServices.getAllJobs();

      runInAction(() => {
        if (response.success) {
          this.allJobs = response.jobs;
          if (this.allJobs.length > 0) {
            this.setSelectedJobId(this.allJobs[0]);
            this.fetchJobDetails(this.allJobs[0]);
          }
        } else {
          this.allJobs = [];
          this.error = response.message || '';
        }
      });
    } catch (error: any) {
      this.allJobs = [];
      this.error = error.message || '';
      toast.error(this.error);
    } finally {
      runInAction(() => {
        this.isAllJobsLoading = false;
      });
    }
  };

  fetchJobDetails = async (job_id: string) => {
    this.isJobDetailsLoading = true;
    try {
      const response = await getAllJobsServices.getJobData(job_id);

      runInAction(() => {
        if (response.success) {
          this.jobDetails = response.job_info;
        } else {
          this.jobDetails = null;
          this.error = response.message || '';
        }
      });
    } catch (error: any) {
      this.jobDetails = null;
      this.error = error.message || '';
      handleApiError(error, 'Error occured while fetching job details');
    } finally {
      runInAction(() => {
        this.isJobDetailsLoading = false;
      });
    }
  };

  // Polling methods (without loading indicators)
  pollAllJobs = async () => {
    this.isPollingJobs = true;
    try {
      const response = await getAllJobsServices.getAllJobs();

      runInAction(() => {
        if (response.success) {
          // Update jobs list if it has changed
          if (JSON.stringify(this.allJobs) !== JSON.stringify(response.jobs)) {
            this.allJobs = response.jobs as string[];

            // If current selected job is no longer in the list, select the first one
            if (
              this.selectedJobId &&
              response.jobs.length > 0 &&
              !response.jobs.includes(this.selectedJobId as never)
            ) {
              this.setSelectedJobId(response.jobs[0] as string);
              this.pollJobDetails(response.jobs[0] as string);
            }
          }
        } else {
          this.error = response.message || '';
        }
      });
    } catch (error: any) {
      this.error = error.message || '';
      // Don't show toast for polling errors to avoid spam
    } finally {
      runInAction(() => {
        this.isPollingJobs = false;
      });
    }
  };

  pollJobDetails = async (job_id: string) => {
    if (!job_id) {
      return;
    }

    this.isPollingJobDetails = true;
    try {
      const response = await getAllJobsServices.getJobData(job_id);

      runInAction(() => {
        if (response.success) {
          // Update job details if they have changed
          if (JSON.stringify(this.jobDetails) !== JSON.stringify(response.job_info)) {
            this.jobDetails = response.job_info;
          }
        } else {
          this.error = response.message || '';
        }
      });
    } catch (error: any) {
      this.error = error.message || '';
      // Don't show toast for polling errors to avoid spam
    } finally {
      runInAction(() => {
        this.isPollingJobDetails = false;
      });
    }
  };

  // Method to poll both jobs and current job details
  pollData = async () => {
    await this.pollAllJobs();
    if (this.selectedJobId) {
      await this.pollJobDetails(this.selectedJobId);
    }
  };
}
